import { extractImageFrames, clampTo } from '../../tools';
import type { TencentModelConfig } from '../types';

/** 腾讯云 ExtInfo 必须是 string；高码率参数以 JSON 字符串传入 */
function buildSeedanceExtInfo(providerOptions?: Record<string, unknown>): string | undefined {
    if (providerOptions?.high_bitrate !== true) return undefined;
    return JSON.stringify({ AdditionalParameters: { bitrate_mode: 'high' } });
}

export const seedanceConfigs: Record<string, TencentModelConfig> = {
    'seedance-2.0': {
        enableTranslation: false,
        statusMapping: {
            taskIdPath: 'Response.TaskId',
            success: ['FINISH'],
            failed: ['FAIL'],
            processing: ['WAITING', 'PROCESSING'],
            statePath: 'Response.Status',
            videoUrlPath: 'Response.AigcVideoTask.Output.FileInfos[0].FileUrl',
            errorPath: 'Response.AigcVideoTask.Message',
            errCodePath: 'Response.AigcVideoTask.ErrCode',
        },
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, videoUris, audioUri, audioUris, aspectRatio, duration, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            const images = (imagePrompt ?? []).filter(Boolean).slice(0, 9);
            const allMedia = [
                ...((videoUris?.length ?? 0) > 0 ? videoUris! : videoUri ? [videoUri] : []).filter(Boolean).map(url => ({ type: 'video' as const, url })),
                ...((audioUris?.length ?? 0) > 0 ? audioUris! : audioUri ? [audioUri] : []).filter(Boolean).map(url => ({ type: 'audio' as const, url })),
            ].slice(0, 3);
            const videos = allMedia.filter(m => m.type === 'video').map(m => m.url);
            const audios = allMedia.filter(m => m.type === 'audio').map(m => m.url);
            // 首帧模式
            if (frameMode === 'first') {
                return {
                    ModelName: 'VS',
                    ModelVersion: '2.0',
                    FileInfos: [
                        {
                            Type: 'Url',
                            Category: 'Image',
                            Url: firstImage,
                            Usage: 'FirstFrame',
                        },
                    ],
                    Prompt: prompt,
                    OutputConfig: {
                        StorageMode: 'Temporary',
                        Duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                        Resolution: clampTo(quality || '480p', ['480p', '720p', '1080p', '2k', '4k'] as const, '480p').toUpperCase(),
                        AspectRatio: aspectRatio === 'match'
                            ? 'adaptive'
                            : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                        AudioGeneration: typeof providerOptions?.audio === 'boolean'
                            ? (providerOptions.audio === true ? 'Enabled' : 'Disabled')
                            : 'Enabled',
                        LogoAdd: typeof providerOptions?.watermark === 'boolean'
                            ? (providerOptions.watermark === true ? 'Enabled' : 'Disabled')
                            : 'Disabled',
                    },
                    // SD2 高码率功能（ExtInfo 须为 string）
                    ExtInfo: buildSeedanceExtInfo(providerOptions),
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    ModelName: 'VS',
                    ModelVersion: '2.0',
                    FileInfos: [
                        {
                            Type: 'Url',
                            Category: 'Image',
                            Url: firstImage,
                            Usage: 'FirstFrame',
                        },
                    ],
                    LastFrameUrl: lastImage || firstImage,
                    Prompt: prompt,
                    OutputConfig: {
                        StorageMode: 'Temporary',
                        Duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                        Resolution: clampTo(quality || '480p', ['480p', '720p', '1080p', '2k', '4k'] as const, '480p').toUpperCase(),
                        AspectRatio: aspectRatio === 'match'
                            ? 'adaptive'
                            : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                        AudioGeneration: typeof providerOptions?.audio === 'boolean'
                            ? (providerOptions.audio === true ? 'Enabled' : 'Disabled')
                            : 'Enabled',
                        LogoAdd: typeof providerOptions?.watermark === 'boolean'
                            ? (providerOptions.watermark === true ? 'Enabled' : 'Disabled')
                            : 'Disabled',
                    },
                    // SD2 高码率功能（ExtInfo 须为 string）
                    ExtInfo: buildSeedanceExtInfo(providerOptions),
                };
            // 参考模式
            } else if (frameMode === 'ref') {
                return {
                    ModelName: 'VS',
                    ModelVersion: '2.0',
                    FileInfos: [
                        ...images.map(url => ({
                            Type: 'Url',
                            Category: 'Image',
                            Url: url,
                            Usage: 'Reference',
                        })),
                        ...videos.map(url => ({
                            Type: 'Url',
                            Category: 'Video',
                            Url: url,
                            Usage: 'Reference',
                        })),
                        ...audios.map(url => ({
                            Type: 'Url',
                            Category: 'Audio',
                            Url: url,
                            Usage: 'Reference',
                        })),
                    ],
                    Prompt: prompt,
                    OutputConfig: {
                        StorageMode: 'Temporary',
                        Duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                        Resolution: clampTo(quality || '480p', ['480p', '720p', '1080p', '2k', '4k'] as const, '480p').toUpperCase(),
                        AspectRatio: aspectRatio === 'match'
                            ? 'adaptive'
                            : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                        AudioGeneration: typeof providerOptions?.audio === 'boolean'
                            ? (providerOptions.audio === true ? 'Enabled' : 'Disabled')
                            : 'Enabled',
                        LogoAdd: typeof providerOptions?.watermark === 'boolean'
                            ? (providerOptions.watermark === true ? 'Enabled' : 'Disabled')
                            : 'Disabled',
                    },
                    // SD2 高码率功能（ExtInfo 须为 string）
                    ExtInfo: buildSeedanceExtInfo(providerOptions),
                };
            // 文生视频模式
            } else {
                return {
                    ModelName: 'VS',
                    ModelVersion: '2.0',
                    Prompt: prompt,
                    OutputConfig: {
                        StorageMode: 'Temporary',
                        Duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                        Resolution: clampTo(quality || '480p', ['480p', '720p', '1080p', '2k', '4k'] as const, '480p').toUpperCase(),
                        AspectRatio: aspectRatio === 'match'
                            ? 'adaptive'
                            : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                        AudioGeneration: typeof providerOptions?.audio === 'boolean'
                            ? (providerOptions.audio === true ? 'Enabled' : 'Disabled')
                            : 'Enabled',
                        LogoAdd: typeof providerOptions?.watermark === 'boolean'
                            ? (providerOptions.watermark === true ? 'Enabled' : 'Disabled')
                            : 'Disabled',
                    },
                    // SD2 高码率功能（ExtInfo 须为 string）
                    ExtInfo: buildSeedanceExtInfo(providerOptions),
                };
            }
        },
    },
    'seedance-2.0-fast': {
        enableTranslation: false,
        statusMapping: {
            taskIdPath: 'Response.TaskId',
            success: ['FINISH'],
            failed: ['FAIL'],
            processing: ['WAITING', 'PROCESSING'],
            statePath: 'Response.Status',
            videoUrlPath: 'Response.AigcVideoTask.Output.FileInfos[0].FileUrl',
            errorPath: 'Response.AigcVideoTask.Message',
            errCodePath: 'Response.AigcVideoTask.ErrCode',
        },
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, videoUris, audioUri, audioUris, aspectRatio, duration, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            const images = (imagePrompt ?? []).filter(Boolean).slice(0, 9);
            const allMedia = [
                ...((videoUris?.length ?? 0) > 0 ? videoUris! : videoUri ? [videoUri] : []).filter(Boolean).map(url => ({ type: 'video' as const, url })),
                ...((audioUris?.length ?? 0) > 0 ? audioUris! : audioUri ? [audioUri] : []).filter(Boolean).map(url => ({ type: 'audio' as const, url })),
            ].slice(0, 3);
            const videos = allMedia.filter(m => m.type === 'video').map(m => m.url);
            const audios = allMedia.filter(m => m.type === 'audio').map(m => m.url);
            // 首帧模式
            if (frameMode === 'first') {
                return {
                    ModelName: 'VS',
                    ModelVersion: '2.0-fast',
                    FileInfos: [
                        {
                            Type: 'Url',
                            Category: 'Image',
                            Url: firstImage,
                            Usage: 'FirstFrame',
                        },
                    ],
                    Prompt: prompt,
                    OutputConfig: {
                        StorageMode: 'Temporary',
                        Duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                        Resolution: clampTo(quality || '480p', ['480p', '720p'] as const, '480p').toUpperCase(),
                        AspectRatio: aspectRatio === 'match'
                            ? 'adaptive'
                            : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                        AudioGeneration: typeof providerOptions?.audio === 'boolean'
                            ? (providerOptions.audio === true ? 'Enabled' : 'Disabled')
                            : 'Enabled',
                        LogoAdd: typeof providerOptions?.watermark === 'boolean'
                            ? (providerOptions.watermark === true ? 'Enabled' : 'Disabled')
                            : 'Disabled',
                    },
                    // SD2 高码率功能（ExtInfo 须为 string）
                    ExtInfo: buildSeedanceExtInfo(providerOptions),
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    ModelName: 'VS',
                    ModelVersion: '2.0-fast',
                    FileInfos: [
                        {
                            Type: 'Url',
                            Category: 'Image',
                            Url: firstImage,
                            Usage: 'FirstFrame',
                        },
                    ],
                    LastFrameUrl: lastImage || firstImage,
                    Prompt: prompt,
                    OutputConfig: {
                        StorageMode: 'Temporary',
                        Duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                        Resolution: clampTo(quality || '480p', ['480p', '720p'] as const, '480p').toUpperCase(),
                        AspectRatio: aspectRatio === 'match'
                            ? 'adaptive'
                            : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                        AudioGeneration: typeof providerOptions?.audio === 'boolean'
                            ? (providerOptions.audio === true ? 'Enabled' : 'Disabled')
                            : 'Enabled',
                        LogoAdd: typeof providerOptions?.watermark === 'boolean'
                            ? (providerOptions.watermark === true ? 'Enabled' : 'Disabled')
                            : 'Disabled',
                    },
                    // SD2 高码率功能（ExtInfo 须为 string）
                    ExtInfo: buildSeedanceExtInfo(providerOptions),
                };
            // 参考模式
            } else if (frameMode === 'ref') {
                return {
                    ModelName: 'VS',
                    ModelVersion: '2.0-fast',
                    FileInfos: [
                        ...images.map(url => ({
                            Type: 'Url',
                            Category: 'Image',
                            Url: url,
                            Usage: 'Reference',
                        })),
                        ...videos.map(url => ({
                            Type: 'Url',
                            Category: 'Video',
                            Url: url,
                            Usage: 'Reference',
                        })),
                        ...audios.map(url => ({
                            Type: 'Url',
                            Category: 'Audio',
                            Url: url,
                            Usage: 'Reference',
                        })),
                    ],
                    Prompt: prompt,
                    OutputConfig: {
                        StorageMode: 'Temporary',
                        Duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                        Resolution: clampTo(quality || '480p', ['480p', '720p'] as const, '480p').toUpperCase(),
                        AspectRatio: aspectRatio === 'match'
                            ? 'adaptive'
                            : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                        AudioGeneration: typeof providerOptions?.audio === 'boolean'
                            ? (providerOptions.audio === true ? 'Enabled' : 'Disabled')
                            : 'Enabled',
                        LogoAdd: typeof providerOptions?.watermark === 'boolean'
                            ? (providerOptions.watermark === true ? 'Enabled' : 'Disabled')
                            : 'Disabled',
                    },
                    // SD2 高码率功能（ExtInfo 须为 string）
                    ExtInfo: buildSeedanceExtInfo(providerOptions),
                };
            // 文生视频模式
            } else {
                return {
                    ModelName: 'VS',
                    ModelVersion: '2.0-fast',
                    Prompt: prompt,
                    OutputConfig: {
                        StorageMode: 'Temporary',
                        Duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                        Resolution: clampTo(quality || '480p', ['480p', '720p'] as const, '480p').toUpperCase(),
                        AspectRatio: aspectRatio === 'match'
                            ? 'adaptive'
                            : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                        AudioGeneration: typeof providerOptions?.audio === 'boolean'
                            ? (providerOptions.audio === true ? 'Enabled' : 'Disabled')
                            : 'Enabled',
                        LogoAdd: typeof providerOptions?.watermark === 'boolean'
                            ? (providerOptions.watermark === true ? 'Enabled' : 'Disabled')
                            : 'Disabled',
                    },
                    // SD2 高码率功能（ExtInfo 须为 string）
                    ExtInfo: buildSeedanceExtInfo(providerOptions),
                };
            }
        },
    },
    'seedance-2.0-mini': {
        enableTranslation: false,
        statusMapping: {
            taskIdPath: 'Response.TaskId',
            success: ['FINISH'],
            failed: ['FAIL'],
            processing: ['WAITING', 'PROCESSING'],
            statePath: 'Response.Status',
            videoUrlPath: 'Response.AigcVideoTask.Output.FileInfos[0].FileUrl',
            errorPath: 'Response.AigcVideoTask.Message',
            errCodePath: 'Response.AigcVideoTask.ErrCode',
        },
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, videoUris, audioUri, audioUris, aspectRatio, duration, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            const images = (imagePrompt ?? []).filter(Boolean).slice(0, 9);
            const allMedia = [
                ...((videoUris?.length ?? 0) > 0 ? videoUris! : videoUri ? [videoUri] : []).filter(Boolean).map(url => ({ type: 'video' as const, url })),
                ...((audioUris?.length ?? 0) > 0 ? audioUris! : audioUri ? [audioUri] : []).filter(Boolean).map(url => ({ type: 'audio' as const, url })),
            ].slice(0, 3);
            const videos = allMedia.filter(m => m.type === 'video').map(m => m.url);
            const audios = allMedia.filter(m => m.type === 'audio').map(m => m.url);
            // 首帧模式
            if (frameMode === 'first') {
                return {
                    ModelName: 'VS',
                    ModelVersion: '2.0-mini',
                    FileInfos: [
                        {
                            Type: 'Url',
                            Category: 'Image',
                            Url: firstImage,
                            Usage: 'FirstFrame',
                        },
                    ],
                    Prompt: prompt,
                    OutputConfig: {
                        StorageMode: 'Temporary',
                        Duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                        Resolution: clampTo(quality || '480p', ['480p', '720p'] as const, '480p').toUpperCase(),
                        AspectRatio: aspectRatio === 'match'
                            ? 'adaptive'
                            : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                        AudioGeneration: typeof providerOptions?.audio === 'boolean'
                            ? (providerOptions.audio === true ? 'Enabled' : 'Disabled')
                            : 'Enabled',
                        LogoAdd: typeof providerOptions?.watermark === 'boolean'
                            ? (providerOptions.watermark === true ? 'Enabled' : 'Disabled')
                            : 'Disabled',
                    },
                    // SD2 高码率功能（ExtInfo 须为 string）
                    ExtInfo: buildSeedanceExtInfo(providerOptions),
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    ModelName: 'VS',
                    ModelVersion: '2.0-mini',
                    FileInfos: [
                        {
                            Type: 'Url',
                            Category: 'Image',
                            Url: firstImage,
                            Usage: 'FirstFrame',
                        },
                    ],
                    LastFrameUrl: lastImage || firstImage,
                    Prompt: prompt,
                    OutputConfig: {
                        StorageMode: 'Temporary',
                        Duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                        Resolution: clampTo(quality || '480p', ['480p', '720p'] as const, '480p').toUpperCase(),
                        AspectRatio: aspectRatio === 'match'
                            ? 'adaptive'
                            : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                        AudioGeneration: typeof providerOptions?.audio === 'boolean'
                            ? (providerOptions.audio === true ? 'Enabled' : 'Disabled')
                            : 'Enabled',
                        LogoAdd: typeof providerOptions?.watermark === 'boolean'
                            ? (providerOptions.watermark === true ? 'Enabled' : 'Disabled')
                            : 'Disabled',
                    },
                    // SD2 高码率功能（ExtInfo 须为 string）
                    ExtInfo: buildSeedanceExtInfo(providerOptions),
                };
            // 参考模式
            } else if (frameMode === 'ref') {
                return {
                    ModelName: 'VS',
                    ModelVersion: '2.0-mini',
                    FileInfos: [
                        ...images.map(url => ({
                            Type: 'Url',
                            Category: 'Image',
                            Url: url,
                            Usage: 'Reference',
                        })),
                        ...videos.map(url => ({
                            Type: 'Url',
                            Category: 'Video',
                            Url: url,
                            Usage: 'Reference',
                        })),
                        ...audios.map(url => ({
                            Type: 'Url',
                            Category: 'Audio',
                            Url: url,
                            Usage: 'Reference',
                        })),
                    ],
                    Prompt: prompt,
                    OutputConfig: {
                        StorageMode: 'Temporary',
                        Duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                        Resolution: clampTo(quality || '480p', ['480p', '720p'] as const, '480p').toUpperCase(),
                        AspectRatio: aspectRatio === 'match'
                            ? 'adaptive'
                            : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                        AudioGeneration: typeof providerOptions?.audio === 'boolean'
                            ? (providerOptions.audio === true ? 'Enabled' : 'Disabled')
                            : 'Enabled',
                        LogoAdd: typeof providerOptions?.watermark === 'boolean'
                            ? (providerOptions.watermark === true ? 'Enabled' : 'Disabled')
                            : 'Disabled',
                    },
                    // SD2 高码率功能（ExtInfo 须为 string）
                    ExtInfo: buildSeedanceExtInfo(providerOptions),
                };
            // 文生视频模式
            } else {
                return {
                    ModelName: 'VS',
                    ModelVersion: '2.0-mini',
                    Prompt: prompt,
                    OutputConfig: {
                        StorageMode: 'Temporary',
                        Duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const, 5),
                        Resolution: clampTo(quality || '480p', ['480p', '720p'] as const, '480p').toUpperCase(),
                        AspectRatio: aspectRatio === 'match'
                            ? 'adaptive'
                            : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                        AudioGeneration: typeof providerOptions?.audio === 'boolean'
                            ? (providerOptions.audio === true ? 'Enabled' : 'Disabled')
                            : 'Enabled',
                        LogoAdd: typeof providerOptions?.watermark === 'boolean'
                            ? (providerOptions.watermark === true ? 'Enabled' : 'Disabled')
                            : 'Disabled',
                    },
                    // SD2 高码率功能（ExtInfo 须为 string）
                    ExtInfo: buildSeedanceExtInfo(providerOptions),
                };
            }
        },
    },
    'seedance-2.5': {
        enableTranslation: false,
        statusMapping: {
            taskIdPath: 'Response.TaskId',
            success: ['FINISH'],
            failed: ['FAIL'],
            processing: ['WAITING', 'PROCESSING'],
            statePath: 'Response.Status',
            videoUrlPath: 'Response.AigcVideoTask.Output.FileInfos[0].FileUrl',
            errorPath: 'Response.AigcVideoTask.Message',
            errCodePath: 'Response.AigcVideoTask.ErrCode',
        },
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, videoUris, audioUri, audioUris, aspectRatio, duration, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage, lastImage] = extractImageFrames(imagePrompt);
            const images = (imagePrompt ?? []).filter(Boolean).slice(0, 30);
            const videos = ((videoUris?.length ?? 0) > 0 ? videoUris! : videoUri ? [videoUri] : []).filter(Boolean).slice(0, 10);
            const audios = ((audioUris?.length ?? 0) > 0 ? audioUris! : audioUri ? [audioUri] : []).filter(Boolean).slice(0, 10);
            // 首帧模式
            if (frameMode === 'first') {
                return {
                    ModelName: 'VS',
                    ModelVersion: '2.5',
                    FileInfos: [
                        {
                            Type: 'Url',
                            Category: 'Image',
                            Url: firstImage,
                            Usage: 'FirstFrame',
                        },
                    ],
                    Prompt: prompt,
                    OutputConfig: {
                        StorageMode: 'Temporary',
                        Duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30] as const, 5),
                        Resolution: clampTo(quality || '480p', ['480p', '720p', '1080p'] as const, '480p').toUpperCase(),
                        AspectRatio: 'adaptive',
                        AudioGeneration: typeof providerOptions?.audio === 'boolean'
                            ? (providerOptions.audio === true ? 'Enabled' : 'Disabled')
                            : 'Enabled',
                        LogoAdd: typeof providerOptions?.watermark === 'boolean'
                            ? (providerOptions.watermark === true ? 'Enabled' : 'Disabled')
                            : 'Disabled',
                    },
                    ExtInfo: buildSeedanceExtInfo(providerOptions),
                };
            // 首尾帧模式
            } else if (frameMode === 'first_last') {
                return {
                    ModelName: 'VS',
                    ModelVersion: '2.5',
                    FileInfos: [
                        {
                            Type: 'Url',
                            Category: 'Image',
                            Url: firstImage,
                            Usage: 'FirstFrame',
                        },
                        {
                            Type: 'Url',
                            Category: 'Image',
                            Url: lastImage || firstImage,
                            Usage: 'LastFrame',
                        },
                    ],
                    Prompt: prompt,
                    OutputConfig: {
                        StorageMode: 'Temporary',
                        Duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30] as const, 5),
                        Resolution: clampTo(quality || '480p', ['480p', '720p', '1080p'] as const, '480p').toUpperCase(),
                        AspectRatio: 'adaptive',
                        AudioGeneration: typeof providerOptions?.audio === 'boolean'
                            ? (providerOptions.audio === true ? 'Enabled' : 'Disabled')
                            : 'Enabled',
                        LogoAdd: typeof providerOptions?.watermark === 'boolean'
                            ? (providerOptions.watermark === true ? 'Enabled' : 'Disabled')
                            : 'Disabled',
                    },
                    ExtInfo: buildSeedanceExtInfo(providerOptions),
                };
            // 参考模式
            } else if (frameMode === 'ref') {
                return {
                    ModelName: 'VS',
                    ModelVersion: '2.5',
                    FileInfos: [
                        ...images.map(url => ({
                            Type: 'Url',
                            Category: 'Image',
                            Url: url,
                            Usage: 'Reference',
                        })),
                        ...videos.map(url => ({
                            Type: 'Url',
                            Category: 'Video',
                            Url: url,
                            Usage: 'Reference',
                        })),
                        ...audios.map(url => ({
                            Type: 'Url',
                            Category: 'Audio',
                            Url: url,
                            Usage: 'Reference',
                        })),
                    ],
                    Prompt: prompt,
                    OutputConfig: {
                        StorageMode: 'Temporary',
                        Duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30] as const, 5),
                        Resolution: clampTo(quality || '480p', ['480p', '720p', '1080p'] as const, '480p').toUpperCase(),
                        AspectRatio: aspectRatio === 'match'
                            ? 'adaptive'
                            : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                        AudioGeneration: typeof providerOptions?.audio === 'boolean'
                            ? (providerOptions.audio === true ? 'Enabled' : 'Disabled')
                            : 'Enabled',
                        LogoAdd: typeof providerOptions?.watermark === 'boolean'
                            ? (providerOptions.watermark === true ? 'Enabled' : 'Disabled')
                            : 'Disabled',
                    },
                    ExtInfo: buildSeedanceExtInfo(providerOptions),
                };
            // 文生视频模式
            } else {
                return {
                    ModelName: 'VS',
                    ModelVersion: '2.5',
                    Prompt: prompt,
                    OutputConfig: {
                        StorageMode: 'Temporary',
                        Duration: clampTo(duration || 5, [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30] as const, 5),
                        Resolution: clampTo(quality || '480p', ['480p', '720p', '1080p'] as const, '480p').toUpperCase(),
                        AspectRatio: aspectRatio === 'match'
                            ? 'adaptive'
                            : clampTo(aspectRatio || '16:9', ['9:16', '3:4', '1:1', '4:3', '16:9', '21:9'] as const, '16:9'),
                        AudioGeneration: typeof providerOptions?.audio === 'boolean'
                            ? (providerOptions.audio === true ? 'Enabled' : 'Disabled')
                            : 'Enabled',
                        LogoAdd: typeof providerOptions?.watermark === 'boolean'
                            ? (providerOptions.watermark === true ? 'Enabled' : 'Disabled')
                            : 'Disabled',
                    },
                    ExtInfo: buildSeedanceExtInfo(providerOptions),
                };
            }
        },
    },
    'seedance-2.5-v2v': {
        enableTranslation: false,
        statusMapping: {
            taskIdPath: 'Response.TaskId',
            success: ['FINISH'],
            failed: ['FAIL'],
            processing: ['WAITING', 'PROCESSING'],
            statePath: 'Response.Status',
            videoUrlPath: 'Response.AigcVideoTask.Output.FileInfos[0].FileUrl',
            errorPath: 'Response.AigcVideoTask.Message',
            errCodePath: 'Response.AigcVideoTask.ErrCode',
        },
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, audioUri, audioUris, quality, providerOptions } = params;
            const images = (imagePrompt ?? []).filter(Boolean).slice(0, 30);
            const audios = ((audioUris?.length ?? 0) > 0 ? audioUris! : audioUri ? [audioUri] : []).filter(Boolean).slice(0, 10);
            return {
                ModelName: 'VS',
                ModelVersion: '2.5',
                FileInfos: [
                    {
                        Type: 'Url',
                        Category: 'Video',
                        Url: videoUri,
                        Usage: 'Reference',
                    },
                    ...images.map(url => ({
                        Type: 'Url',
                        Category: 'Image',
                        Url: url,
                        Usage: 'Reference',
                    })),
                    ...audios.map(url => ({
                        Type: 'Url',
                        Category: 'Audio',
                        Url: url,
                        Usage: 'Reference',
                    })),
                ],
                Prompt: prompt,
                OutputConfig: {
                    StorageMode: 'Temporary',
                    Resolution: clampTo(quality || '480p', ['480p', '720p', '1080p'] as const, '480p').toUpperCase(),
                    AspectRatio: 'adaptive',
                    AudioGeneration: typeof providerOptions?.audio === 'boolean'
                        ? (providerOptions.audio === true ? 'Enabled' : 'Disabled')
                        : 'Enabled',
                    LogoAdd: typeof providerOptions?.watermark === 'boolean'
                        ? (providerOptions.watermark === true ? 'Enabled' : 'Disabled')
                        : 'Disabled',
                },
                ExtInfo: buildSeedanceExtInfo(providerOptions),
            };
        },
    },
};